import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Building2, Target, Users, Globe, Upload, X, Check, AlertCircle, Image, Briefcase, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined service/practice area options
const PRACTICE_AREAS = [
  'Personal Injury',
  'Corporate Law',
  'Immigration Law',
  'Family Law',
  'Real Estate Law',
  'Criminal Defense',
  'Estate Planning',
  'Employment Law',
  'Intellectual Property',
  'Bankruptcy Law',
  'Tax Law',
  'Healthcare Law'
];

// Predefined target client types
const CLIENT_TYPES = [
  'Individuals',
  'Businesses',
  'Startups',
  'Corporate Clients',
  'Property Buyers',
  'Families',
  'High Net Worth Individuals',
  'Small Businesses',
  'Enterprise Companies'
];

// Company country (drives the local currency shown in the Growth Snapshot)
const COUNTRY_OPTIONS = [
  'United Kingdom', 'Ireland', 'United States', 'Canada', 'Australia', 'New Zealand',
  'India', 'Singapore', 'South Africa', 'France', 'Germany', 'Spain', 'Italy',
  'Netherlands', 'Belgium', 'Portugal', 'Austria', 'Finland', 'Greece', 'Luxembourg',
  'Sweden', 'Denmark', 'Norway', 'Poland', 'Switzerland',
];

export default function Onboarding({ user }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  
  // Step 1: Firm Details
  const [firmDetails, setFirmDetails] = useState({
    firm_name: user?.firm?.name || '',
    website: '',
    description: '',
    country: '',
    logo: null // Base64 encoded
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState('');
  
  // Step 2: Services & Practice Areas
  const [services, setServices] = useState({
    primary_services: [],
    custom_services: []
  });
  const [customServiceInput, setCustomServiceInput] = useState('');
  
  // Step 3: Target Markets
  const [targetMarkets, setTargetMarkets] = useState({
    geographic_markets: [],
    target_client_types: [],
    additional_notes: ''
  });
  const [geoInput, setGeoInput] = useState('');

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLogoError('');
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setLogoError('Please upload a PNG, JPG, or JPEG file');
      return;
    }
    
    // Validate file size (200KB = 204800 bytes)
    if (file.size > 204800) {
      setLogoError('Logo file size must be under 200KB');
      return;
    }
    
    // Convert to Base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setFirmDetails({ ...firmDetails, logo: base64 });
      setLogoPreview(base64);
    };
    reader.onerror = () => {
      setLogoError('Failed to read file. Please try again.');
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setFirmDetails({ ...firmDetails, logo: null });
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Toggle service selection
  const toggleService = (service) => {
    setServices(prev => {
      if (prev.primary_services.includes(service)) {
        return { ...prev, primary_services: prev.primary_services.filter(s => s !== service) };
      } else {
        return { ...prev, primary_services: [...prev.primary_services, service] };
      }
    });
  };

  // Add custom service
  const addCustomService = () => {
    if (customServiceInput.trim()) {
      setServices(prev => ({
        ...prev,
        custom_services: [...prev.custom_services, customServiceInput.trim()]
      }));
      setCustomServiceInput('');
    }
  };

  // Remove custom service
  const removeCustomService = (index) => {
    setServices(prev => ({
      ...prev,
      custom_services: prev.custom_services.filter((_, i) => i !== index)
    }));
  };

  // Toggle client type
  const toggleClientType = (type) => {
    setTargetMarkets(prev => {
      if (prev.target_client_types.includes(type)) {
        return { ...prev, target_client_types: prev.target_client_types.filter(t => t !== type) };
      } else {
        return { ...prev, target_client_types: [...prev.target_client_types, type] };
      }
    });
  };

  // Add geographic market
  const addGeoMarket = () => {
    if (geoInput.trim()) {
      setTargetMarkets(prev => ({
        ...prev,
        geographic_markets: [...prev.geographic_markets, geoInput.trim()]
      }));
      setGeoInput('');
    }
  };

  // Remove geographic market
  const removeGeoMarket = (index) => {
    setTargetMarkets(prev => ({
      ...prev,
      geographic_markets: prev.geographic_markets.filter((_, i) => i !== index)
    }));
  };

  // Submit Step 1
  const submitStep1 = async () => {
    if (!firmDetails.firm_name.trim()) {
      setError('Please enter your firm name');
      return;
    }
    if (!firmDetails.website.trim()) {
      setError('Please enter your website URL');
      return;
    }
    if (!firmDetails.country) {
      setError('Please select your company country');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/new/step1`, {
        firm_name: firmDetails.firm_name,
        website: firmDetails.website,
        description: firmDetails.description,
        country: firmDetails.country,
        logo: firmDetails.logo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentStep(2);
    } catch (err) {
      console.error('Error saving step 1:', err);
      setError(err.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Submit Step 2
  const submitStep2 = async () => {
    if (services.primary_services.length === 0 && services.custom_services.length === 0) {
      setError('Please select at least one service or practice area');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/new/step2`, {
        primary_services: services.primary_services,
        custom_services: services.custom_services
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentStep(3);
    } catch (err) {
      console.error('Error saving step 2:', err);
      setError(err.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Submit Step 3 (Final)
  const submitStep3 = async () => {
    if (targetMarkets.geographic_markets.length === 0) {
      setError('Please add at least one geographic market');
      return;
    }
    if (targetMarkets.target_client_types.length === 0) {
      setError('Please select at least one target client type');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/new/step3`, {
        geographic_markets: targetMarkets.geographic_markets,
        target_client_types: targetMarkets.target_client_types,
        additional_notes: targetMarkets.additional_notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local storage
      const firmData = JSON.parse(localStorage.getItem('firm') || '{}');
      firmData.onboarding_completed = true;
      if (firmDetails.logo) {
        firmData.logo = firmDetails.logo;
      }
      localStorage.setItem('firm', JSON.stringify(firmData));
      
      // Hard reload so App.js re-reads the updated firm (onboarding_completed)
      // and the plan-selection gate engages for clients without an active plan.
      window.location.assign('/dashboard');
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setError(err.response?.data?.detail || 'Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-indigo-50/50">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-violet-200/40 to-indigo-200/40 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-20 w-60 h-60 bg-gradient-to-br from-emerald-200/30 to-teal-200/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-3xl mx-auto px-4 py-12">
        {/* Progress Indicator */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-600">Step {currentStep} of 3</span>
            <span className="text-sm text-slate-500">
              {currentStep === 1 && 'Firm Details'}
              {currentStep === 2 && 'Services & Practice Areas'}
              {currentStep === 3 && 'Target Markets'}
            </span>
          </div>
          
          <div className="flex gap-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                  step <= currentStep 
                    ? 'bg-gradient-to-r from-violet-500 to-indigo-600' 
                    : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 animate-shake">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 1: Firm Details */}
        {currentStep === 1 && (
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-white/50 p-8 shadow-xl animate-fadeInUp" data-testid="onboarding-step1">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Tell Us About Your Firm</h1>
                <p className="text-slate-500">Basic information to personalize your dashboard</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Firm Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Firm / Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={firmDetails.firm_name}
                  onChange={(e) => setFirmDetails({ ...firmDetails, firm_name: e.target.value })}
                  placeholder="e.g., Smith & Associates Law Firm"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="firm-name-input"
                />
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Website URL <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={firmDetails.website}
                    onChange={(e) => setFirmDetails({ ...firmDetails, website: e.target.value })}
                    placeholder="e.g., www.smithlaw.com"
                    className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    data-testid="website-input"
                  />
                </div>
              </div>

              {/* Company Country */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Company Country <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                  <select
                    value={firmDetails.country}
                    onChange={(e) => setFirmDetails({ ...firmDetails, country: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white appearance-none"
                    data-testid="country-select"
                  >
                    <option value="">Select your country…</option>
                    {COUNTRY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <p className="mt-1.5 text-xs text-slate-400">Used to show potential revenue in your local currency.</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Short Firm Description
                </label>
                <textarea
                  value={firmDetails.description}
                  onChange={(e) => setFirmDetails({ ...firmDetails, description: e.target.value })}
                  placeholder="Brief description of your firm and areas of expertise..."
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                  data-testid="description-input"
                />
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Upload Logo
                </label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-violet-400 transition-all">
                  {logoPreview ? (
                    <div className="flex flex-col items-center">
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="max-h-24 max-w-full object-contain rounded-lg mb-4"
                        data-testid="logo-preview"
                      />
                      <button
                        onClick={removeLogo}
                        className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <X className="w-4 h-4" />
                        Remove Logo
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="cursor-pointer"
                    >
                      <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center">
                        <Image className="w-8 h-8 text-violet-500" />
                      </div>
                      <p className="text-slate-600 font-medium mb-1">Click to upload your logo</p>
                      <p className="text-sm text-slate-400">PNG, JPG, or JPEG (max 200KB)</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleLogoUpload}
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                </div>
                {logoError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {logoError}
                  </p>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-end mt-8 pt-6 border-t border-slate-100">
              <button
                onClick={submitStep1}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-violet-500/30 transition-all disabled:opacity-50"
                data-testid="step1-next-btn"
              >
                {loading ? 'Saving...' : 'Continue'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Services & Practice Areas */}
        {currentStep === 2 && (
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-white/50 p-8 shadow-xl animate-fadeInUp" data-testid="onboarding-step2">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>What Services Should We Focus On?</h1>
                <p className="text-slate-500">Select your primary practice areas for visibility optimization</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Practice Areas Grid */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Primary Practice Areas / Services
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {PRACTICE_AREAS.map((area) => (
                    <button
                      key={area}
                      onClick={() => toggleService(area)}
                      className={`p-3 rounded-xl text-left text-sm font-medium transition-all ${
                        services.primary_services.includes(area)
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/30'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`service-${area.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <div className="flex items-center gap-2">
                        {services.primary_services.includes(area) && (
                          <Check className="w-4 h-4" />
                        )}
                        {area}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Other/Custom Services */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Other Services (Specify)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customServiceInput}
                    onChange={(e) => setCustomServiceInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCustomService()}
                    placeholder="Enter custom service"
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                    data-testid="custom-service-input"
                  />
                  <button
                    onClick={addCustomService}
                    className="px-5 py-3 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-900 transition-all"
                    data-testid="add-custom-service-btn"
                  >
                    Add
                  </button>
                </div>
                
                {/* Custom services list */}
                {services.custom_services.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {services.custom_services.map((service, idx) => (
                      <span 
                        key={idx}
                        className="flex items-center gap-1 px-3 py-1.5 bg-violet-100 text-violet-700 rounded-lg text-sm font-medium"
                      >
                        {service}
                        <button onClick={() => removeCustomService(idx)} className="hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected count */}
              <div className="p-4 bg-emerald-50 rounded-xl">
                <p className="text-sm text-emerald-700">
                  <span className="font-semibold">{services.primary_services.length + services.custom_services.length}</span> service(s) selected
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
              <button
                onClick={() => setCurrentStep(1)}
                className="flex items-center gap-2 px-6 py-3 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={submitStep2}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-violet-500/30 transition-all disabled:opacity-50"
                data-testid="step2-next-btn"
              >
                {loading ? 'Saving...' : 'Continue'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Target Markets & Clients */}
        {currentStep === 3 && (
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-white/50 p-8 shadow-xl animate-fadeInUp" data-testid="onboarding-step3">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>Who Should Find Your Firm Online?</h1>
                <p className="text-slate-500">Define your target markets and ideal clients</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Geographic Markets */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Primary Geographic Markets <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-slate-500 mb-3">Add cities, regions, or countries you want to target</p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={geoInput}
                      onChange={(e) => setGeoInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addGeoMarket()}
                      placeholder="e.g., New York City, California, United States"
                      className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                      data-testid="geo-market-input"
                    />
                  </div>
                  <button
                    onClick={addGeoMarket}
                    className="px-5 py-3 bg-slate-800 text-white rounded-xl font-medium hover:bg-slate-900 transition-all"
                    data-testid="add-geo-market-btn"
                  >
                    Add
                  </button>
                </div>
                
                {/* Geographic markets list */}
                {targetMarkets.geographic_markets.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {targetMarkets.geographic_markets.map((market, idx) => (
                      <span 
                        key={idx}
                        className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium"
                      >
                        <MapPin className="w-3 h-3" />
                        {market}
                        <button onClick={() => removeGeoMarket(idx)} className="hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Target Client Types */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Target Client Types <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {CLIENT_TYPES.map((type) => (
                    <button
                      key={type}
                      onClick={() => toggleClientType(type)}
                      className={`p-3 rounded-xl text-left text-sm font-medium transition-all ${
                        targetMarkets.target_client_types.includes(type)
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/30'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`client-type-${type.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <div className="flex items-center gap-2">
                        {targetMarkets.target_client_types.includes(type) && (
                          <Check className="w-4 h-4" />
                        )}
                        {type}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Additional Target Audience Notes
                </label>
                <textarea
                  value={targetMarkets.additional_notes}
                  onChange={(e) => setTargetMarkets({ ...targetMarkets, additional_notes: e.target.value })}
                  placeholder="Any specific characteristics of your ideal clients..."
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                  data-testid="additional-notes-input"
                />
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
              <button
                onClick={() => setCurrentStep(2)}
                className="flex items-center gap-2 px-6 py-3 border border-slate-200 text-slate-700 rounded-xl font-medium hover:bg-slate-50 transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={submitStep3}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-violet-500/30 transition-all disabled:opacity-50"
                data-testid="complete-onboarding-btn"
              >
                {loading ? 'Completing...' : 'Complete Setup'}
                <Check className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
